// src/components/RecommendedDeclinedEditor.js
// v3.5.81: Editor for the "Recommended but Declined" section of a proposal.
//
// Per the v3.5.81 design call (Confirm 3 — Explicit), the rep affirmatively
// flags items they recommended but the client declined. Products in the
// catalog have a `recommendation_reason` column that provides default
// messaging; the rep can override per-flag, and add a `decline_reason`
// describing the client's position. The flagged items render as the
// "Recommended but Declined" section on the public proposal page — the
// breach-defense paper trail.
//
// Stored shape (on quote.inputs.declinedItems and on the proposal record):
//   [{ product_id, name, recommendation_reason, decline_reason }]
//
// Reusable across all quote types — Voice, Bundle, Multi-Site, FlexIT
// versions can pass their respective product catalogs and persistence
// hooks. v3.5.81 only wires it into Managed IT (QuotePage); subsequent
// versions extend.

import React, { useState, useMemo } from 'react';

const PALETTE = {
  bg:        '#fffbeb',
  border:    '#fde68a',
  accent:    '#92400e',
  accentBg:  '#fef3c7',
  ghost:     '#fef9c3',
};

export default function RecommendedDeclinedEditor({
  products = [],
  declinedItems = [],
  onChange,
  selectedProductIds = [],   // optional — used to filter the picker so we
                             // don't suggest products that are already in
                             // the quote (you can't decline something you
                             // already selected).
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');

  const declinedIds = useMemo(
    () => new Set(declinedItems.map(d => d.product_id)),
    [declinedItems]
  );
  const selectedSet = useMemo(
    () => new Set(selectedProductIds || []),
    [selectedProductIds]
  );

  // Pickable products: anything in the catalog NOT already selected and NOT
  // already declined. Filter by search query if present. Internal-only
  // products are hidden — they're never client-facing.
  const pickable = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    return products
      .filter(p => !p.internal_only)
      .filter(p => !selectedSet.has(p.id))
      .filter(p => !declinedIds.has(p.id))
      .filter(p => !q || (p.name || '').toLowerCase().includes(q) || (p.category || '').toLowerCase().includes(q))
      .slice(0, 50);
  }, [products, pickerQuery, declinedIds, selectedSet]);

  function addDeclined(product) {
    const next = [
      ...declinedItems,
      {
        product_id: product.id,
        name: product.name,
        // Pre-fill from catalog. Rep can override.
        recommendation_reason: product.recommendation_reason || '',
        decline_reason: '',
      },
    ];
    onChange?.(next);
    setPickerOpen(false);
    setPickerQuery('');
  }

  function removeDeclined(idx) {
    const next = declinedItems.slice();
    next.splice(idx, 1);
    onChange?.(next);
  }

  function updateField(idx, field, value) {
    const next = declinedItems.slice();
    next[idx] = { ...next[idx], [field]: value };
    onChange?.(next);
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.intro}>
        Flag any service you recommended but the client elected not to include. These appear on the
        client-facing proposal as a clearly marked &ldquo;Not Selected&rdquo; section, so the scope of
        what was offered &mdash; and what was declined &mdash; is documented for everyone&rsquo;s
        protection.
      </div>

      {declinedItems.length === 0 && !pickerOpen && (
        <div style={styles.empty}>No services flagged yet.</div>
      )}

      {declinedItems.map((d, idx) => (
        <div key={d.product_id || idx} style={styles.card}>
          <div style={styles.cardHead}>
            <div style={styles.cardName}>{d.name || '(unnamed)'}</div>
            <button onClick={() => removeDeclined(idx)} style={styles.removeBtn} type="button">
              Remove
            </button>
          </div>

          <label style={styles.label}>
            <span>Why we recommended this</span>
            <textarea
              value={d.recommendation_reason || ''}
              onChange={e => updateField(idx, 'recommendation_reason', e.target.value)}
              placeholder="Pre-filled from the product catalog. Override if you want to be more specific to this client."
              rows={2}
              style={styles.textarea}
            />
          </label>

          <label style={styles.label}>
            <span>Client&rsquo;s position (optional)</span>
            <textarea
              value={d.decline_reason || ''}
              onChange={e => updateField(idx, 'decline_reason', e.target.value)}
              placeholder="e.g. Cost concerns; planning to revisit Q3; existing tool covers this; risk acknowledged."
              rows={2}
              style={styles.textarea}
            />
          </label>
        </div>
      ))}

      {pickerOpen ? (
        <div style={styles.pickerWrap}>
          <input
            autoFocus
            value={pickerQuery}
            onChange={e => setPickerQuery(e.target.value)}
            placeholder="Search products by name or category..."
            style={styles.pickerInput}
          />
          <div style={styles.pickerList}>
            {pickable.length === 0 && (
              <div style={styles.pickerEmpty}>
                {pickerQuery ? 'No matches.' : 'All catalog products are already selected or declined.'}
              </div>
            )}
            {pickable.map(p => (
              <div
                key={p.id}
                onClick={() => addDeclined(p)}
                style={styles.pickerItem}
                onMouseEnter={e => e.currentTarget.style.background = PALETTE.accentBg}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}
              >
                <div style={styles.pickerItemName}>{p.name}</div>
                <div style={styles.pickerItemMeta}>{p.category || '—'}</div>
              </div>
            ))}
          </div>
          <div style={styles.pickerActions}>
            <button onClick={() => { setPickerOpen(false); setPickerQuery(''); }} style={styles.cancelBtn} type="button">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setPickerOpen(true)} style={styles.addBtn} type="button">
          + Flag a recommended service the client declined
        </button>
      )}
    </div>
  );
}

const styles = {
  wrap: {
    background: PALETTE.bg,
    border: `1px solid ${PALETTE.border}`,
    borderRadius: 8,
    padding: 14,
    marginTop: 12,
  },
  intro: {
    fontSize: 11,
    color: PALETTE.accent,
    background: PALETTE.accentBg,
    borderLeft: `3px solid ${PALETTE.accent}`,
    padding: '8px 12px',
    borderRadius: 3,
    marginBottom: 12,
    lineHeight: 1.5,
  },
  empty: {
    fontSize: 11,
    color: '#9ca3af',
    fontStyle: 'italic',
    padding: '12px 4px',
    textAlign: 'center',
  },
  card: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    padding: 12,
    marginBottom: 10,
  },
  cardHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  cardName: {
    fontSize: 13,
    fontWeight: 700,
    color: '#1f2937',
  },
  removeBtn: {
    padding: '3px 8px',
    background: 'transparent',
    border: '1px solid #fecaca',
    borderRadius: 3,
    fontSize: 10,
    color: '#991b1b',
    cursor: 'pointer',
    fontWeight: 600,
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginBottom: 8,
    fontSize: 11,
    color: '#374151',
    fontWeight: 600,
  },
  textarea: {
    padding: 8,
    border: '1px solid #d1d5db',
    borderRadius: 4,
    fontSize: 12,
    fontFamily: 'inherit',
    resize: 'vertical',
    fontWeight: 400,
    color: '#1f2937',
  },
  addBtn: {
    width: '100%',
    padding: '8px 12px',
    background: 'white',
    border: `1px dashed ${PALETTE.accent}`,
    borderRadius: 5,
    fontSize: 11,
    color: PALETTE.accent,
    cursor: 'pointer',
    fontWeight: 600,
  },
  pickerWrap: {
    background: 'white',
    border: `1px solid ${PALETTE.border}`,
    borderRadius: 6,
    padding: 10,
  },
  pickerInput: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 4,
    fontSize: 12,
    marginBottom: 8,
    boxSizing: 'border-box',
  },
  pickerList: {
    maxHeight: 240,
    overflowY: 'auto',
    border: '1px solid #f3f4f6',
    borderRadius: 4,
    background: 'white',
  },
  pickerItem: {
    padding: '8px 10px',
    cursor: 'pointer',
    borderBottom: '1px solid #f9fafb',
  },
  pickerItemName: {
    fontSize: 12,
    fontWeight: 600,
    color: '#1f2937',
  },
  pickerItemMeta: {
    fontSize: 10,
    color: '#6b7280',
    marginTop: 2,
  },
  pickerEmpty: {
    padding: 16,
    fontSize: 11,
    color: '#9ca3af',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  pickerActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  cancelBtn: {
    padding: '4px 10px',
    background: 'white',
    border: '1px solid #d1d5db',
    borderRadius: 4,
    fontSize: 11,
    color: '#6b7280',
    cursor: 'pointer',
  },
};
